import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Empty,
  Page,
  Spinner,
  api,
  brl,
  dateOnly,
  dateTime,
  km,
  rideStatusLabel,
  useAuth,
  BUILD_ID,
} from '@mobi/web';
import { useDriverStatus, type DriverRide } from '../useDriver.js';
import { EarningsSummary } from '../components/EarningsSummary.js';

interface HistoryRide extends DriverRide {
  timestamps: Record<string, string | null>;
}

export function Earnings() {
  const { user, logout } = useAuth();
  const { status } = useDriverStatus();
  const [rides, setRides] = useState<HistoryRide[] | null>(null);

  useEffect(() => {
    api<{ rides: HistoryRide[] }>('/api/rides?limit=50')
      .then((r) => setRides(r.rides))
      .catch(() => setRides([]));
  }, []);

  return (
    <Page title="Seus ganhos" subtitle={user?.name}>
      <EarningsSummary />

        {status?.subscription && (
          <Card className="mt-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-semibold">Plano {status.subscription.planName}</p>
                <p className="text-sm text-muted">
                  válido até {dateOnly(status.subscription.expiresOn)}
                </p>
              </div>
              <Badge tone={status.subscription.daysLeft <= 3 ? 'warn' : 'good'}>
                {status.subscription.daysLeft} dia{status.subscription.daysLeft === 1 ? '' : 's'}
              </Badge>
            </div>
            {status.subscriptionWarning && (
              <p className="mt-3 text-sm text-warn">{status.subscriptionWarning}</p>
            )}
          </Card>
        )}

        <h2 className="mt-8 font-semibold">Últimas corridas</h2>

        {!rides && (
          <div className="mt-8 grid place-items-center text-brand">
            <Spinner />
          </div>
        )}

        {rides?.length === 0 && (
          <Empty title="Nenhuma corrida ainda" hint="Fique online para começar a receber." />
        )}

        <ul className="mt-3 space-y-3">
          {rides?.map((ride) => (
            <li key={ride.id}><Card className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{ride.destination.address}</p>
                  <p className="truncate text-sm text-muted">de {ride.origin.address}</p>
                  <p className="mt-1 text-xs text-faint">
                    {dateTime(ride.timestamps.finishedAt ?? ride.timestamps.requestedAt)} ·{' '}
                    {km(ride.estimate.distanceMeters)}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="tabular font-semibold">
                    {brl(ride.finalFareCents ?? ride.estimate.fareCents)}
                  </p>
                  <div className="mt-1">
                    <Badge tone={ride.status === 'finished' ? 'good' : 'neutral'}>
                      {rideStatusLabel(ride.status)}
                    </Badge>
                  </div>
                </div>
              </div>

              {ride.walletAppliedCents > 0 && (
                <p className="mt-2 text-xs text-muted">
                  {brl(ride.cashDueCents)} recebidos em dinheiro ·{' '}
                  {brl(ride.walletAppliedCents)} do saldo do passageiro
                </p>
              )}
              </Card></li>
          ))}
        </ul>

      <div className="mt-10">
        <Button variant="outline" full onClick={() => void logout()}>
          Sair da conta
        </Button>
        {/* Serve para saber, olhando a tela, se o aparelho pegou a versão
            nova — sem isso, "atualizou?" vira adivinhação. */}
        <p className="mt-4 text-center text-xs text-faint">versão {BUILD_ID}</p>
      </div>
    </Page>
  );
}
